import { MultilingualString } from "features/shared/types/string";
import { Modal } from "./modals";
import { Property, SubProduct } from "./subproduct";
import { ColumnGroup, ProductTable, TableColumn } from "./subproductTable";

// for subProduct reducer
export enum SubProductAction {
    ASSIGN_PROPERTY = "[SubProductAction] ASSIGN_PROPERTY",
    ASSIGN_SUBPRODUCT = "[SubProductAction] ADD_SUBPRODUCT",
    ADD_TABLE = "[SubProductAction] ADD_TABLE",
    REMOVE_PROPERTY = "[SubProductAction] REMOVE_PROPERTY",
    REMOVE_SUBPRODUCT = "[SubProductAction] REMOVE_SUBPRODUCT",
    REMOVE_TABLE = "[SubProductAction] REMOVE_TABLE",
    MOVE_AND_INSERT_SUBPRODUCT = "[SubProductAction] MOVE_AND_INSERT_SUBPRODUCT",
    MOVE_AND_INSERT_PROPERTY = "[SubProductAction] MOVE_AND_INSERT_PROPERTY",
    MOVE_AND_INSERT_TABLE = "[SubProductAction] MOVE_AND_INSERT_TABLE",
    RESET_TABLE_COLUMNS = "[SubProductAction] RESET_TABLE_COLUMNS",
    SET_TABLES = "[SubProductAction] SET_TABLES",
    SET_TABLE_IDS = "[SubProductAction] SET_TABLE_IDS",
    SET_SHOWN_TABLE_ID = "[SubProductAction] SET_SHOWN_TABLE_ID",
    SET_TEMP_SHOWN_TABLE = "[SubProductAction] SET_TEMP_SHOWN_TABLE",
    SET_TABLE_NAME = "[SubProductAction] SET_TABLE_NAME",
    SET_TABLE_DATA = "[SubProductAction] SET_TABLE_DATA",
    SET_SUBPRODUCT_PROPERTY_VALUE = "[SubProductAction] SET_SUBPRODUCT_PROPERTY_VALUE",
    SET_SUBPRODUCT_IMAGE_VALUE = "[SubProductAction] SET_SUBPRODUCT_IMAGE_VALUE",
    SET_READONLY_MODE = "[SubProductAction] SET_READONLY_MODE",
    SET_GROUPING_MODE = "[SubProductAction] SET_GROUPING_MODE",
    SET_COLUMN_GROUPS = "[SubProductAction] SET_COLUMN_GROUPS",
    TOGGLE_LOCALE_SHOWN = "[SubProductAction] TOGGLE_LOCALE_SHOWN",
    TOGGLE_COLUMN_SHOWN = "[SubProductAction] TOGGLE_COLUMN_SHOWN",
    TOGGLE_ROW_SHOWN = "[SubProductAction] TOGGLE_ROW_SHOWN",
    OPEN_MODAL = "[SubProductAction] OPEN_MODAL",
    CLOSE_MODAL = "[SubProductAction] CLOSE_MODAL",
}

interface AssignProperty {
    type: SubProductAction.ASSIGN_PROPERTY;
    propertyColumn: TableColumn;
}

interface AssignSubProduct {
    type: SubProductAction.ASSIGN_SUBPRODUCT;
    subProduct: SubProduct;
}

interface AddTable {
    type: SubProductAction.ADD_TABLE;
}

interface RemoveProperty {
    type: SubProductAction.REMOVE_PROPERTY;
    accessor: string;
    idx: number; // allows for faster access
}

interface RemoveSubProduct {
    type: SubProductAction.REMOVE_SUBPRODUCT;
    id: string;
    idx: number; // allows for faster access
}

interface RemoveTable {
    type: SubProductAction.REMOVE_TABLE;
    id: string;
}

interface MoveAndInsertSubProduct {
    type: SubProductAction.MOVE_AND_INSERT_SUBPRODUCT;
    oldIdx: number;
    newIdx: number;
}

interface MoveAndInsertProperty {
    type: SubProductAction.MOVE_AND_INSERT_PROPERTY;
    oldIdx: number;
    newIdx: number;
}

interface MoveAndInsertTable {
    type: SubProductAction.MOVE_AND_INSERT_TABLE;
    oldIdx: number;
    newIdx: number;
}

interface ResetTableColumns {
    type: SubProductAction.RESET_TABLE_COLUMNS;
}

interface SetTables {
    type: SubProductAction.SET_TABLES;
    tables: Record<string, ProductTable>;
}

interface SetTableIds {
    type: SubProductAction.SET_TABLE_IDS;
    tableIds: string[];
}

interface SetShownTableId {
    type: SubProductAction.SET_SHOWN_TABLE_ID;
    id: string;
}

interface SetTempShownTable {
    type: SubProductAction.SET_TEMP_SHOWN_TABLE;
    table: Partial<ProductTable>;
}

interface SetTableName {
    type: SubProductAction.SET_TABLE_NAME;
    name: MultilingualString;
}

interface SetTableData {
    type: SubProductAction.SET_TABLE_DATA;

    id: string;
    data: Partial<ProductTable>;
}

interface SetSubProductProperty {
    type: SubProductAction.SET_SUBPRODUCT_PROPERTY_VALUE;
    subProductId: string;
    accessor: string;
    propertyValue: Property["value"];
}

interface SetSubProductImage {
    type: SubProductAction.SET_SUBPRODUCT_IMAGE_VALUE;
    subProductId: string;
    imageUrl: string;
}

interface SetGroupingMode {
    type: SubProductAction.SET_GROUPING_MODE;
    groupingMode: boolean;
}
interface SetReadonlyMode {
    type: SubProductAction.SET_READONLY_MODE;
    isReadOnly: boolean;
}

interface SetColumnGroups {
    type: SubProductAction.SET_COLUMN_GROUPS;
    columnGroups: ColumnGroup[];
}

interface ToggleLocaleShown {
    type: SubProductAction.TOGGLE_LOCALE_SHOWN;
}

interface ToggleColumnShown {
    type: SubProductAction.TOGGLE_COLUMN_SHOWN;
    idx: number;
}

interface ToggleRowShown {
    type: SubProductAction.TOGGLE_ROW_SHOWN;
    subProductId: string;
}

interface OpenModal {
    type: SubProductAction.OPEN_MODAL;
    modal: Modal;
}

interface CloseModal {
    type: SubProductAction.CLOSE_MODAL;
    id: string;
}

export type SubProductActionTypes =
    | AssignProperty
    | AssignSubProduct
    | AddTable
    | RemoveProperty
    | RemoveSubProduct
    | RemoveTable
    | MoveAndInsertSubProduct
    | MoveAndInsertProperty
    | MoveAndInsertTable
    | ResetTableColumns
    | SetTables
    | SetTableIds
    | SetShownTableId
    | SetTempShownTable
    | SetTableName
    | SetTableData
    | SetSubProductProperty
    | SetSubProductImage
    | SetGroupingMode
    | SetReadonlyMode
    | SetColumnGroups
    | ToggleLocaleShown
    | ToggleColumnShown
    | ToggleRowShown
    | OpenModal
    | CloseModal;
